import { useEffect, useRef } from "react";

/**
 * FlowField — canvas particle flow that reads like moving water/current.
 * Sky-blue streams drift left-to-right over the near-black brand colour,
 * evolve over time, and swirl away from the pointer.
 */
export default function FlowField({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0;
    let h = 0;
    let raf = null;
    let running = false;
    let t = 0;
    const mouse = { x: -9999, y: -9999 };
    let particles = [];
    // palette read from CSS variables so the field follows the active theme
    const pal = { base: "10, 15, 26", brand: "61, 181, 255", bright: "190, 230, 255" };
    const readPalette = () => {
      const cs = getComputedStyle(document.documentElement);
      const rgb = (name) => cs.getPropertyValue(name).trim().split(/\s+/).join(", ");
      pal.base = rgb("--c-base") || pal.base;
      pal.brand = rgb("--c-brand") || pal.brand;
      pal.bright = document.documentElement.classList.contains("light") ? rgb("--c-ink") : "190, 230, 255";
    };

    const resize = () => {
      readPalette();
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = `rgb(${pal.base})`;
      ctx.fillRect(0, 0, w, h);
      const count = Math.max(160, Math.min(850, Math.round((w * h) / 2200)));
      particles = Array.from({ length: count }, () => spawn());
    };

    const spawn = () => {
      const x = Math.random() * w;
      const y = Math.random() * h;
      return {
        x, y, px: x, py: y,
        speed: 0.4 + Math.random() * 1.1,
        bright: Math.random() < 0.08,
        life: 120 + Math.random() * 260,
      };
    };

    // Smooth layered-trig flow field (cheap, organic — like water current)
    const angleAt = (x, y, tt) => {
      return (
        Math.sin(x * 0.0021 + tt * 0.35) * 1.4 +
        Math.cos(y * 0.0028 - tt * 0.27) * 1.2 +
        Math.sin((x + y) * 0.0011 + tt * 0.18) * 0.9
      );
    };

    const step = () => {
      t += 0.008;
      // trail fade
      ctx.fillStyle = `rgba(${pal.base}, 0.07)`;
      ctx.fillRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const a = angleAt(p.x, p.y, t);
        // base flow + gentle rightward current
        let vx = Math.cos(a) * p.speed + 0.55;
        let vy = Math.sin(a) * p.speed * 0.8;
        // pointer swirl
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 16900) {
          const d = Math.sqrt(d2) || 1;
          const f = (130 - d) / 130;
          vx += (dx / d) * f * 3.2;
          vy += (dy / d) * f * 3.2;
        }
        p.px = p.x;
        p.py = p.y;
        p.x += vx;
        p.y += vy;
        p.life -= 1;

        if (p.x < -10 || p.x > w + 10 || p.y < -10 || p.y > h + 10 || p.life <= 0) {
          particles[i] = spawn();
          continue;
        }

        const sp = Math.min(1, (Math.abs(vx) + Math.abs(vy)) / 3);
        ctx.strokeStyle = p.bright
          ? `rgba(${pal.bright}, ${0.25 + sp * 0.45})`
          : `rgba(${pal.brand}, ${0.08 + sp * 0.3})`;
        ctx.lineWidth = p.bright ? 1.4 : 1;
        ctx.beginPath();
        ctx.moveTo(p.px, p.py);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      raf = requestAnimationFrame(step);
    };

    const start = () => {
      if (!running && !reduced) {
        running = true;
        raf = requestAnimationFrame(step);
      }
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      mouse.x = cx - rect.left;
      mouse.y = cy - rect.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };

    resize();
    if (reduced) {
      // static frame for reduced motion
      t = 4;
      for (let s = 0; s < 90; s++) {
        ctx.fillStyle = `rgba(${pal.base}, 0.07)`;
        ctx.fillRect(0, 0, w, h);
        particles.forEach((p, i) => {
          const a = angleAt(p.x, p.y, t);
          p.px = p.x; p.py = p.y;
          p.x += Math.cos(a) * p.speed + 0.55;
          p.y += Math.sin(a) * p.speed * 0.8;
          ctx.strokeStyle = `rgba(${pal.brand}, 0.18)`;
          ctx.beginPath(); ctx.moveTo(p.px, p.py); ctx.lineTo(p.x, p.y); ctx.stroke();
        });
      }
    }

    const io = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? start() : stop()),
      { threshold: 0.05 }
    );
    io.observe(canvas);

    window.addEventListener("resize", resize);
    const themeObserver = new MutationObserver(resize);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    canvas.parentElement?.addEventListener("pointermove", onMove);
    canvas.parentElement?.addEventListener("pointerleave", onLeave);

    return () => {
      stop();
      io.disconnect();
      themeObserver.disconnect();
      window.removeEventListener("resize", resize);
      canvas.parentElement?.removeEventListener("pointermove", onMove);
      canvas.parentElement?.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="flow-field-canvas"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
